(() => {
  if (customElements.get('oracle-portal')) return;
  class OraclePortal extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.video = this.querySelector('video');
      this.sound = this.querySelector('[data-sound]');
      this.toggle = this.querySelector('[data-toggle]');
      this.status = this.querySelector('[role="status"]');
      this.phrases = [...this.querySelectorAll('.op-phrase')];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.small = matchMedia('(max-width: 900px)');
      this.userPaused = this.userPaused || false;
      this.desired = !this.userPaused && !this.motion.matches && !navigator.connection?.saveData;
      this.opted = false;
      this.visible = true;
      this.failed = false;
      this.ticket = 0;
      this.video.muted = true;
      this.sound.addEventListener('click', () => {
        this.video.muted = !this.video.muted;
        this.labels();
      }, {signal});
      this.toggle.addEventListener('click', () => {
        this.desired = this.video.paused;
        this.userPaused = !this.desired;
        if (this.desired) this.opted = true;
        this.reconcile();
      }, {signal});
      this.video.addEventListener('playing', () => {
        this.starting = false;
        this.setAttribute('data-loaded', '');
        this.setAttribute('data-playing', '');
        this.removeAttribute('data-static');
        this.status.textContent = '';
        this.labels();
        this.tick();
      }, {signal});
      this.video.addEventListener('pause', () => {
        this.removeAttribute('data-playing');
        cancelAnimationFrame(this.frame);
        this.labels();
      }, {signal});
      this.video.addEventListener('error', () => this.fallback('Animation unavailable. Navigation remains available.', true), {signal});
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.video.muted = true;
        this.reconcile();
      }, {signal});
      this.motion.addEventListener('change', () => {
        this.opted = false;
        this.desired = !this.userPaused && !this.motion.matches && !navigator.connection?.saveData;
        this.video.muted = true;
        this.reconcile();
      }, {signal});
      this.small.addEventListener('change', () => this.render(), {signal});
      const viewportChanged = () => {
        this.render();
        this.reconcile();
      };
      window.addEventListener('resize', viewportChanged, {signal});
      window.addEventListener('scroll', viewportChanged, {signal, passive:true});
      window.visualViewport?.addEventListener('resize', viewportChanged, {signal});
      this.resizeObserver = new ResizeObserver(() => {
        this.render();
        if (this.small.matches && this.video.paused && this.desired) this.reconcile();
      });
      this.resizeObserver.observe(this);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (!this.visible) this.video.muted = true;
        this.reconcile();
      }, {threshold:0});
      this.observer.observe(this);
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(this)) this.disconnectedCallback();
      }, {signal});
      this.setAttribute('data-ready','');
      this.reconcile();
    }
    labels() {
      const on = !this.video.muted;
      this.sound.textContent = on ? 'Sound off' : 'Sound on';
      this.sound.setAttribute('aria-pressed', String(on));
      this.sound.disabled = this.failed;
      this.toggle.textContent = this.video.paused ? 'Play animation' : 'Pause animation';
      this.toggle.setAttribute('aria-pressed', String(this.video.paused));
      this.toggle.disabled = this.failed || this.starting;
    }
    fallback(message, failed = false) {
      this.desired = false;
      this.failed = failed;
      this.starting = false;
      this.ticket++;
      this.video.pause();
      this.video.muted = true;
      this.removeAttribute('data-playing');
      this.setAttribute('data-static','');
      this.status.textContent = message;
      this.render(true);
      this.labels();
    }
    reconcile() {
      const ticket = ++this.ticket;
      const staticMode = !this.opted && (this.motion.matches || navigator.connection?.saveData);
      if (staticMode) {
        this.setAttribute('data-static','');
        this.render(true);
      }
      if (!this.desired || this.failed || !this.visible || document.hidden || staticMode) {
        this.starting = false;
        this.video.pause();
        this.labels();
        return;
      }
      if (this.small.matches && !this.opted && this.video.currentTime < 3.95 && !this.flightVisible()) {
        this.starting = false;
        this.video.pause();
        this.setAttribute('data-static','');
        this.render(true);
        this.labels();
        return;
      }
      this.removeAttribute('data-static');
      if (!this.video.querySelector('source[src]')) {
        this.video.querySelectorAll('source').forEach(source => source.src = source.dataset.src);
        this.video.load();
      }
      if (!this.video.paused) {
        this.labels();
        return;
      }
      this.starting = true;
      this.video.play().then(() => {
        if (!this.abort || ticket !== this.ticket) {
          if (!this.desired || !this.visible || document.hidden) this.video.pause();
          return;
        }
        this.starting = false;
        this.labels();
      }).catch(() => {
        if (this.abort && ticket === this.ticket) this.fallback('Autoplay is paused. Select Play animation to start.');
      });
      this.labels();
    }
    phraseCenter(phrase) {
      // Offset layout coordinates remain stable while the phrase is transformed.
      let x = phrase.offsetWidth / 2, y = phrase.offsetHeight / 2;
      for (let node = phrase; node && node !== this; node = node.offsetParent) {
        x += node.offsetLeft;
        y += node.offsetTop;
      }
      return {x,y};
    }
    portalCenter() {
      const hero = this.getBoundingClientRect();
      const stage = this.querySelector('.op-stage').getBoundingClientRect();
      const ratio = (this.video.videoWidth || 1920) / (this.video.videoHeight || 1080);
      const factor = this.small.matches
        ? Math.min(stage.width / ratio, stage.height)
        : Math.max(stage.width / ratio, stage.height);
      const width = factor * ratio, height = factor;
      return {
        x: stage.left - hero.left + (stage.width-width)/2 + width*.71875,
        y: stage.top - hero.top + (stage.height-height)/2 + height*(410/1080)
      };
    }
    flightVisible() {
      const hero = this.getBoundingClientRect();
      const portalY = hero.top + this.portalCenter().y;
      const top = window.visualViewport?.offsetTop || 0;
      const bottom = top + (window.visualViewport?.height || window.innerHeight);
      // Visitors must see the emergence; settled text may be below the fold.
      return portalY >= top+12 && portalY <= bottom-12;
    }
    render(staticMode = this.hasAttribute('data-static')) {
      const time = this.video.currentTime || 0;
      this.phrases.forEach((phrase, index) => {
        let scale = 1, dx = 0, dy = 0, opacity = 1;
        const progress = Math.min(1, Math.max(0, (time - [.45,1.35,2.25][index]) / 1.7));
        if (!staticMode && progress < 1) {
          const origin = this.portalCenter();
          const base = this.phraseCenter(phrase);
          const travel = Math.max(0, (progress-.22)/.78);
          const eased = this.small.matches ? travel*travel*(3-2*travel) : 1 - Math.pow(1-progress, 3);
          dx = (origin.x - base.x) * (1-eased);
          dy = (origin.y - base.y) * (1-eased);
          if (this.small.matches) {
            const growth = Math.min(1,progress/.22);
            scale = progress < .22 ? .04 + .72*growth*growth*(3-2*growth)
              : progress < .80 ? .76 + .36*((progress-.22)/.58)
              : 1.12 - .12*((progress-.80)/.20);
            opacity = progress === 0 ? 0 : Math.min(1,progress*12);
          } else {
            scale = progress < .78 ? .04 + 1.23 * Math.pow(progress/.78, 1.5) : 1.27 - .27*((progress-.78)/.22);
            opacity = progress === 0 ? 0 : Math.min(1,progress*5);
          }
        }
        phrase.style.transform = `translate3d(${dx}px,${dy}px,0) scale(${scale})`;
        phrase.style.opacity = opacity;
        const pulse = staticMode ? .45 : .58 + .20*Math.sin(time*2.1-index);
        phrase.style.filter = `drop-shadow(0 0 ${staticMode?3:4+2*Math.sin(time*1.6)}px var(--op-color))`;
        phrase.querySelectorAll('path').forEach((path,j) => {
          path.style.strokeDashoffset = staticMode ? '0' : String(-time*38-index*40-j*65);
          path.style.opacity = pulse;
        });
      });
      this.style.setProperty('--op-sheen', staticMode ? '-120%' : `${((time%6)/6)*240-120}%`);
      this.style.setProperty('--op-text-sheen', staticMode ? '-120%' : `${120-((time%8)/8)*240}%`);
    }
    tick() {
      cancelAnimationFrame(this.frame);
      if (!this.abort || this.video.paused) return;
      if (this.small.matches && !this.opted && this.video.currentTime < 3.95 && !this.flightVisible()) {
        this.reconcile();
        return;
      }
      this.render(false);
      this.frame = requestAnimationFrame(() => this.tick());
    }
    disconnectedCallback() {
      this.ticket++;
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.abort?.abort();
      this.abort = null;
      this.video?.pause();
    }
  }
  customElements.define('oracle-portal', OraclePortal);
})();
